import type { ServiceSignal, ServiceSignalKind } from '../../api/client'

export type StatusEntry = { kind: ServiceSignalKind; detail: string; since: string; retryAt: string | null }
export type StatusSnapshot = { entries: readonly StatusEntry[]; outage: StatusEntry | null; lastSyncedAt: string | null }

export type ServiceStatusStore = {
  snapshot(): StatusSnapshot
  subscribe(listener: () => void): () => void
  report(signal: ServiceSignal, retryAt?: string | null): void
  clear(kind: ServiceSignalKind): void
  markSynced(at: string): void
  sweep(): void
  reset(): void
}

const DEFAULT_TTL_MS = 5 * 60 * 1000

type StoredEntry = StatusEntry & { lastSeen: string }

export const createServiceStatusStore = (options: { ttlMs?: number; now?: () => Date } = {}): ServiceStatusStore => {
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS
  const now = options.now ?? (() => new Date())
  let records: readonly StoredEntry[] = []
  let lastSyncedAt: string | null = null
  let listeners: readonly (() => void)[] = []
  let cached: StatusSnapshot = { entries: [], outage: null, lastSyncedAt: null }
  let cacheKey = ''

  const emit = () => {
    for (const listener of listeners) listener()
  }

  const live = (): readonly StoredEntry[] => {
    const cutoff = now().getTime() - ttlMs
    return records.filter((record) => Date.parse(record.lastSeen) >= cutoff)
  }

  const snapshot = (): StatusSnapshot => {
    const current = live()
    const key = `${lastSyncedAt ?? ''}|${current.map((record) => `${record.kind}:${record.since}:${record.detail}`).join('|')}`
    if (key === cacheKey) return cached
    cacheKey = key
    cached = {
      entries: current
        .filter((record) => record.kind !== 'database_outage')
        .map(({ lastSeen: _lastSeen, ...entry }) => entry),
      outage: current.find((record) => record.kind === 'database_outage') ?? null,
      lastSyncedAt,
    }
    return cached
  }

  return {
    snapshot,
    subscribe: (listener) => {
      listeners = [...listeners, listener]
      return () => {
        listeners = listeners.filter((entry) => entry !== listener)
      }
    },
    report: (signal, retryAt = null) => {
      const existing = records.find((record) => record.kind === signal.kind)
      const next: StoredEntry = {
        kind: signal.kind,
        detail: signal.detail,
        since: existing?.since ?? signal.at,
        retryAt,
        lastSeen: signal.at,
      }
      records = [...records.filter((record) => record.kind !== signal.kind), next]
      emit()
    },
    clear: (kind) => {
      records = records.filter((record) => record.kind !== kind)
      emit()
    },
    markSynced: (at) => {
      lastSyncedAt = at
      emit()
    },
    sweep: () => {
      const next = live()
      if (next.length !== records.length) {
        records = next
        emit()
      }
    },
    reset: () => {
      records = []
      lastSyncedAt = null
      emit()
    },
  }
}
